import React, { FC, memo, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  FormChildQuestionDto,
  FormQuestionDto,
  QuestionConditionEnum,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import { Autocomplete, TextField } from "@components/base";

interface Select {
  parentQuestionCode?: string;
  question: FormQuestionDto | FormChildQuestionDto;
  disabled?: boolean;
  rowIndex?: number;
}

const Select: FC<Select> = ({
  question,
  disabled,
  parentQuestionCode,
  rowIndex,
}) => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const label = (question as FormQuestionDto).placeholder || t("common.select");

  const questionsToWatch = useMemo(() => {
    if (isEmpty(question.answerOptions)) return [];

    const hasParent = !!parentQuestionCode;
    const hasRow = !!rowIndex;

    const buildCode = (variable: string): string => {
      if (hasParent && !hasRow) return `${parentQuestionCode}-${variable}`;
      if (hasParent && hasRow)
        return `${parentQuestionCode}-${variable}-${rowIndex}`;
      if (!hasParent && hasRow) return `${variable}-${rowIndex}`;

      return variable;
    };

    const codes = question.answerOptions
      ?.flatMap((o) => o?.conditions || [])
      .map((c) => buildCode(c.variable));

    return Array.from(new Set(codes));
  }, [question, parentQuestionCode, rowIndex]);

  const watchedValues = watch(questionsToWatch);

  const conditionalAnswerOptions = useMemo(() => {
    if (!question.answerOptions || isEmpty(questionsToWatch)) {
      return question.answerOptions;
    }

    return question.answerOptions.filter((option) => {
      if (!option.conditions || isEmpty(option.conditions)) {
        return true;
      }

      return option.conditions.every((condition) => {
        if (condition.condition === QuestionConditionEnum.Equals) {
          const value = watchedValues[0];

          return condition.value.includes(value);
        }

        // TODO: Implement other condition types if needed
        return false;
      });
    });
  }, [watchedValues]);

  return (
    <Controller
      name={
        parentQuestionCode ? `${parentQuestionCode}-${question.code}` : question.code
      }
      control={control}
      render={({ field }) => (
        <Autocomplete
          options={conditionalAnswerOptions ?? []}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(a, b) => a.code === b.code}
          value={
            conditionalAnswerOptions?.find((o) => o.code === field.value) ?? null
          }
          onChange={(_, opt) => field.onChange(opt?.code ?? "")}
          disabled={disabled}
          renderInput={(params) => (
            <TextField {...params} variant="filled" placeholder={label} />
          )}
        />
      )}
    />
  );
};

export default memo(Select);
